import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Switch, Typography } from "@mui/material";
import { ConfirmDialog, TextField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import {
  PaymentLinkSubscription,
  useSubscriptionData,
} from "hooks/useSubscriptionData";

interface CancelSubscriptionModalProps {
  open: boolean;
  subscription: PaymentLinkSubscription | null;
  onClose: () => void;
  onDone: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  open,
  subscription,
  onClose,
  onDone,
}) => {
  const { t } = useTranslation();
  const { cancel, loading } = useSubscriptionData();
  const [atPeriodEnd, setAtPeriodEnd] = useState(false);
  const [reason, setReason] = useState("");

  const handleConfirm = async () => {
    if (!subscription) return;

    const succeeded = await cancel(
      subscription.id,
      atPeriodEnd,
      reason.trim() || undefined,
    );
    if (succeeded) {
      setReason("");
      setAtPeriodEnd(false);
      onDone();
      onClose();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      tone="danger"
      title={t("Cancel subscription")}
      message={
        <Stack gap="var(--space-4)">
          <Typography variant="body2">
            {t("subscription.CancelWarning", {
              payer: subscription?.payerName || subscription?.payerEmail || "",
            })}
          </Typography>
          <Stack gap="var(--space-3)">
            <FormControlLabel
              control={
                <Switch
                  checked={atPeriodEnd}
                  onChange={(event) => setAtPeriodEnd(event.target.checked)}
                />
              }
              label={t("Let the paid period finish first")}
            />
            <TextField
              fullWidth
              label={t("Reason")}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Stack>
        </Stack>
      }
      confirmLabel={t("Cancel Subscription")}
      cancelLabel={t("Keep Subscription")}
      busy={loading}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (loading) return;
        onClose();
      }}
    />
  );
};

export default CancelSubscriptionModal;
